// Local sprite store.
//
// Goal: when a Pokémon is confirmed during a live scan (or any OCR flow that
// produces a cropped sprite), persist a small image crop on the user's device
// so it can be reused as the Box / detail thumbnail later. The full
// screenshot is NEVER stored — only the sprite-area crop the OCR module
// already extracts (e.g. detailOcr's `spriteDataUrl`, inventoryOcr's per-cell
// sprite). This keeps the on-device footprint tiny and avoids retaining
// chat / nameplate / IV-bar pixels that the OCR pass walks past.
//
// Storage:
//   - localStorage (web + iOS/Android WKWebView) is the primary store. A
//     ~96×96 JPEG crop is ~5-20 KB, which fits comfortably in the 5 MB per-
//     origin budget even after dozens of species.
//   - Capacitor Preferences mirror (best-effort, fire-and-forget) so the
//     entry survives a webview cold start where localStorage is cleared by
//     some iOS power-management edge cases. Same approach as themePersist.ts.
//   - An LRU bound (default 256 entries) prevents long sessions from filling
//     the quota and tripping the iOS storage exception. Oldest entries are
//     evicted on each write that pushes us over the cap.
//
// Key scheme:
//   `gx.sprite.v1.<fingerprint>` where the fingerprint is built from the
//   record's stable identity:
//     species{speciesId}_cp{cp|na}_shiny{0|1}_src{box|detail|catch}
//   The fingerprint is exposed via `buildSpriteFingerprint` so the same
//   string can be attached to the record/projection later and used as the
//   lookup key in Box UI without re-deriving it.
//
// Failure handling:
//   Every write is wrapped in try/catch. A storage failure NEVER blocks
//   scan ingestion — it just logs a warning and skips the save. The
//   live-scan modal calls `saveSpriteForRecord` after pushing the row to
//   `confirmedRowsRef`, so even if the store is full / disabled the
//   confirmed-rows POST and counter strip behave identically.

const KEY_PREFIX = "gx.sprite.v1.";
const INDEX_KEY = "gx.sprite.v1.__index";
const DEFAULT_LRU_CAP = 256;

/**
 * Custom event dispatched on `window` whenever a sprite is added or
 * removed from the store. The Dex grid + detail sheet subscribe so
 * their thumbnails refresh as soon as a Live Scan flush persists a new
 * crop, without polling localStorage on every render.
 */
export const SPRITE_STORE_EVENT = "gx:sprite-store-updated";

function emitSpriteStoreEvent(detail: { speciesId: number; key: string }): void {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(SPRITE_STORE_EVENT, { detail }));
  } catch {
    /* SSR or non-DOM runtime — silent. */
  }
}

export type SpriteSource = "box" | "detail" | "deep" | "catch" | "pokegenie" | "manual";

/**
 * v1.4.1 b6 (Push 2): per-source priority. A later lower-priority crop
 * (e.g. a box scan) must NEVER overwrite a higher-priority crop already
 * stored for the same species (e.g. a detail / deep scan). Same priority
 * may update only when newer evidence is genuinely better — the caller
 * passes `quality` in that case; an absent quality means "newer is fine,
 * peer source".
 */
export const SPRITE_SOURCE_PRIORITY: Record<SpriteSource, number> = {
  deep:       100,
  detail:     90,
  pokegenie:  60,
  catch:      40,
  box:        20,
  manual:     10,
};

export interface SpriteFingerprintInput {
  speciesId: number | null | undefined;
  cp?: number | null;
  isShiny?: boolean | null;
  source?: SpriteSource | string | null;
}

export interface StoredSpriteMeta {
  /** Storage key (without prefix). Equal to the fingerprint. */
  key: string;
  /** ms epoch the entry was first saved. */
  savedAt: number;
  /** Approximate byte length of the dataUrl (UTF-16 length × 2 in worst case). */
  approxBytes: number;
  /** Where the crop came from. Useful for diagnostics + UI labelling. */
  source: SpriteSource | string;
  /** speciesId mirrored from the fingerprint for ergonomic lookups. */
  speciesId: number;
}

interface IndexEntry {
  key: string;
  savedAt: number;
  bytes: number;
  source: string;
  speciesId: number;
}

interface IndexShape {
  entries: IndexEntry[];
}

/**
 * Build the stable storage fingerprint for a confirmed row. Returns null
 * when the input is missing a speciesId — without that the entry can never
 * be looked up so there's no point storing it.
 */
export function buildSpriteFingerprint(input: SpriteFingerprintInput): string | null {
  const speciesId = numOrNull(input.speciesId);
  if (speciesId === null || speciesId <= 0) return null;
  const cpPart = numOrNull(input.cp);
  const cpToken = cpPart === null ? "na" : String(Math.round(cpPart));
  const shinyToken = input.isShiny ? "1" : "0";
  const sourceToken = normaliseSource(input.source);
  return `species${speciesId}_cp${cpToken}_shiny${shinyToken}_src${sourceToken}`;
}

function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function normaliseSource(v: SpriteFingerprintInput["source"]): SpriteSource {
  if (typeof v !== "string") return "manual";
  const lc = v.toLowerCase();
  // "deep" / "deep_scan" must be matched BEFORE "detail" because the deep-
  // scan source string occasionally carries "detail" as a substring.
  if (lc.includes("deep")) return "deep";
  if (lc.includes("box") || lc === "box_scan") return "box";
  if (lc.includes("detail") || lc === "detail_ocr") return "detail";
  if (lc.includes("catch")) return "catch";
  if (lc.includes("pokegenie")) return "pokegenie";
  return "manual";
}

// ── storage shims ───────────────────────────────────────────────────────────

function readLocal(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): boolean {
  try {
    if (typeof window === "undefined") return false;
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeLocal(key: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

async function writeCapacitorPrefs(key: string, value: string): Promise<void> {
  try {
    const mod = await import("@capacitor/preferences" as any);
    const Preferences = mod?.Preferences;
    if (!Preferences) return;
    await Preferences.set({ key, value });
  } catch {
    /* not on a Capacitor runtime, or value too large; localStorage is
       the source of truth. */
  }
}

// ── index management ────────────────────────────────────────────────────────

function readIndex(): IndexShape {
  const raw = readLocal(INDEX_KEY);
  if (!raw) return { entries: [] };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.entries)) {
      return {
        entries: parsed.entries.filter(
          (e: unknown): e is IndexEntry =>
            !!e
            && typeof (e as IndexEntry).key === "string"
            && typeof (e as IndexEntry).savedAt === "number",
        ),
      };
    }
  } catch {
    /* malformed — drop and start clean. */
  }
  return { entries: [] };
}

function writeIndex(idx: IndexShape): void {
  try {
    writeLocal(INDEX_KEY, JSON.stringify(idx));
  } catch {
    /* ignore */
  }
}

/**
 * Pure helper: compute the entries to evict so the index stays under the
 * cap. Returns the keys to remove (oldest savedAt first). Exposed for
 * tests; the runtime caller (`enforceCap`) wraps it with the actual
 * removal calls.
 */
export function pickEvictionKeys(idx: IndexShape, cap: number): string[] {
  if (cap <= 0) return idx.entries.map((e) => e.key);
  if (idx.entries.length <= cap) return [];
  const sorted = [...idx.entries].sort((a, b) => a.savedAt - b.savedAt);
  const overflow = sorted.length - cap;
  return sorted.slice(0, overflow).map((e) => e.key);
}

function enforceCap(idx: IndexShape, cap: number): IndexShape {
  const toEvict = pickEvictionKeys(idx, cap);
  if (toEvict.length === 0) return idx;
  const evictSet = new Set(toEvict);
  for (const k of toEvict) removeLocal(KEY_PREFIX + k);
  return { entries: idx.entries.filter((e) => !evictSet.has(e.key)) };
}

// ── crop recompression ──────────────────────────────────────────────────────

export interface RecompressOptions {
  /** Max edge of the output sprite, in CSS pixels. Defaults to 96. */
  maxEdge?: number;
  /** JPEG quality 0-1. Defaults to 0.78. */
  quality?: number;
}

/**
 * Re-encode a sprite dataUrl into a small JPEG so the on-device store
 * holds a thumbnail rather than the original crop bytes. Returns the
 * input unchanged when no DOM is available (tests, SSR) so the helper
 * stays pure-by-default — the test suite exercises fingerprinting +
 * eviction without needing a canvas.
 */
export async function recompressSpriteCrop(
  dataUrl: string,
  options: RecompressOptions = {},
): Promise<string> {
  if (!dataUrl) return "";
  if (typeof document === "undefined" || typeof Image === "undefined") return dataUrl;
  const maxEdge = Math.max(16, Math.min(512, options.maxEdge ?? 96));
  const quality = Math.max(0.3, Math.min(0.95, options.quality ?? 0.78));
  try {
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("sprite_image_decode_failed"));
      el.src = dataUrl;
    });
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) return dataUrl;
    const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
    const outW = Math.max(1, Math.round(srcW * scale));
    const outH = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, outW, outH);
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return dataUrl;
  }
}

// ── public API ──────────────────────────────────────────────────────────────

export interface SaveSpriteInput {
  fingerprint: SpriteFingerprintInput;
  /** Raw sprite-area data URL. Re-encoded before storage. */
  dataUrl: string | null | undefined;
  /** Override the default LRU cap (entries). */
  lruCap?: number;
  /** Override compression. */
  compression?: RecompressOptions;
}

export interface SaveSpriteResult {
  saved: boolean;
  key: string | null;
  reason?:
    | "no_sprite_data"
    | "no_fingerprint"
    | "no_storage"
    | "quota_exceeded"
    | "already_stored"
    | "lower_priority_skipped";
  bytes?: number;
}

/**
 * v1.4.1 b6 (Push 2): true when the species already has a higher-priority
 * sprite stored than the source we're about to write. Used by
 * `saveSpriteForRecord` to refuse box crops when a detail/deep crop is
 * already on disk for the same species. Public for selectors / tests.
 */
export function hasHigherPrioritySpriteForSpecies(
  speciesId: number,
  candidateSource: SpriteSource,
): boolean {
  if (!speciesId || speciesId <= 0) return false;
  const candidatePri = SPRITE_SOURCE_PRIORITY[candidateSource] ?? 0;
  const all = listStoredSprites().filter((e) => e.speciesId === speciesId);
  for (const m of all) {
    const src = normaliseSource(m.source);
    const pri = SPRITE_SOURCE_PRIORITY[src] ?? 0;
    if (pri > candidatePri) return true;
  }
  return false;
}

/**
 * Persist the sprite crop for a confirmed record, keyed by the supplied
 * fingerprint. Idempotent: a second call with the same fingerprint is a
 * no-op (so re-confirmation doesn't churn the LRU). Storage failures are
 * caught — the caller can ignore the result and the scan pipeline keeps
 * working.
 */
export async function saveSpriteForRecord(
  input: SaveSpriteInput,
): Promise<SaveSpriteResult> {
  if (!input.dataUrl) return { saved: false, key: null, reason: "no_sprite_data" };
  const key = buildSpriteFingerprint(input.fingerprint);
  if (!key) return { saved: false, key: null, reason: "no_fingerprint" };

  const storageKey = KEY_PREFIX + key;
  const idx = readIndex();
  if (idx.entries.some((e) => e.key === key) && readLocal(storageKey)) {
    return { saved: false, key, reason: "already_stored" };
  }

  // v1.4.1 b6 (Push 2): refuse to write a lower-priority sprite when a
  // higher-priority one already exists for this species. The trainer's
  // detail / deep crops always win over a later box scan.
  const speciesIdGuard = numOrNull(input.fingerprint.speciesId) ?? 0;
  const candidateSource = normaliseSource(input.fingerprint.source);
  if (
    speciesIdGuard > 0 &&
    hasHigherPrioritySpriteForSpecies(speciesIdGuard, candidateSource)
  ) {
    return { saved: false, key, reason: "lower_priority_skipped" };
  }

  const compressed = await recompressSpriteCrop(input.dataUrl, input.compression);
  if (!compressed) return { saved: false, key, reason: "no_sprite_data" };

  const ok = writeLocal(storageKey, compressed);
  if (!ok) {
    // Try evicting one entry and retrying once — handles the iOS
    // localStorage QuotaExceededError after a long session.
    const evicted = enforceCap(idx, Math.max(1, (input.lruCap ?? DEFAULT_LRU_CAP) - 1));
    writeIndex(evicted);
    const retry = writeLocal(storageKey, compressed);
    if (!retry) return { saved: false, key, reason: "quota_exceeded" };
  }

  const speciesId = numOrNull(input.fingerprint.speciesId) ?? 0;
  const sourceToken = normaliseSource(input.fingerprint.source);
  const updated: IndexShape = {
    entries: [
      ...idx.entries.filter((e) => e.key !== key),
      {
        key,
        savedAt: Date.now(),
        bytes: compressed.length,
        source: sourceToken,
        speciesId,
      },
    ],
  };
  const trimmed = enforceCap(updated, input.lruCap ?? DEFAULT_LRU_CAP);
  writeIndex(trimmed);

  // Native mirror, fire-and-forget.
  void writeCapacitorPrefs(storageKey, compressed);

  // Notify subscribed UI (Dex grid, detail sheet) so the new crop shows
  // immediately rather than only after a remount / next mount cycle.
  emitSpriteStoreEvent({ speciesId, key });

  return { saved: true, key, bytes: compressed.length };
}

/**
 * Read back the stored sprite crop for a fingerprint, or null if absent.
 * Synchronous (localStorage). Callers wanting a Capacitor Preferences
 * fallback should layer on top.
 */
export function readSpriteForRecord(
  fingerprint: SpriteFingerprintInput,
): string | null {
  const key = buildSpriteFingerprint(fingerprint);
  if (!key) return null;
  return readLocal(KEY_PREFIX + key);
}

/**
 * Index of every sprite currently in the local store. Lightweight enough
 * to enumerate from the diagnostics panel.
 */
export function listStoredSprites(): StoredSpriteMeta[] {
  return readIndex().entries.map((e) => ({
    key: e.key,
    savedAt: e.savedAt,
    approxBytes: e.bytes,
    source: e.source,
    speciesId: e.speciesId,
  }));
}

interface ParsedSpriteKey {
  ok: boolean;
  cp: number | null;
  shiny: boolean;
  source: string;
}

/**
 * Parse a sprite store key back into its component fields. Exposed so
 * higher-level lookup helpers can filter on shiny / source without
 * re-implementing the regex.
 */
export function parseSpriteKey(key: string): ParsedSpriteKey {
  const m = key.match(/^species\d+_cp([^_]+)_shiny([01])_src(\w+)$/);
  if (!m) return { ok: false, cp: null, shiny: false, source: "" };
  return {
    ok: true,
    cp: m[1] === "na" ? null : Number(m[1]),
    shiny: m[2] === "1",
    source: m[3],
  };
}

/**
 * v1.3.2(7): centralised "best captured crop for this species" lookup.
 *
 * Walks every entry the local store knows about for `speciesId`,
 * newest-first, and returns the freshest data URL whose shiny flag
 * matches `prefs.isShiny` (first pass). If no shiny-matching entry has
 * a payload, falls back to the freshest captured crop of any flag — a
 * shiny tile is still better than the silhouette when only a normal
 * crop is on disk and vice versa.
 *
 * Returns null when the species has no captured crop in this store,
 * which lets callers fall back to the silhouette atlas.
 */
export function findStoredSpriteForSpecies(
  speciesId: number,
  prefs?: { isShiny?: boolean | null },
): { dataUrl: string; meta: StoredSpriteMeta } | null {
  if (!speciesId || speciesId <= 0) return null;
  const all = listStoredSprites().filter((e) => e.speciesId === speciesId);
  if (all.length === 0) return null;
  // v1.4.1 b6 (Push 2): prefer higher-priority sources first (deep/detail
  // > box). Tiebreak by savedAt newest-first so a fresher detail crop
  // beats an older detail crop.
  all.sort((a, b) => {
    const pa = SPRITE_SOURCE_PRIORITY[normaliseSource(a.source)] ?? 0;
    const pb = SPRITE_SOURCE_PRIORITY[normaliseSource(b.source)] ?? 0;
    if (pa !== pb) return pb - pa;
    return b.savedAt - a.savedAt;
  });
  const wantShiny = !!prefs?.isShiny;
  const passes: Array<(m: StoredSpriteMeta) => boolean> = [
    (m) => parseSpriteKey(m.key).shiny === wantShiny,
    () => true,
  ];
  for (const ok of passes) {
    for (const m of all) {
      if (!ok(m)) continue;
      const parts = parseSpriteKey(m.key);
      if (!parts.ok) continue;
      const data = readLocal(KEY_PREFIX + m.key);
      if (data) return { dataUrl: data, meta: m };
    }
  }
  return null;
}

/**
 * v1.3.2(7): lightweight diagnostic for the "sprite exists but grid
 * cannot resolve it" case. Returns a compact summary the caller can
 * route into liveDebugLog / console without dragging the binary
 * payloads through the log channel. Useful when a Dex tile is rendering
 * a silhouette but the trainer is sure they have a captured crop.
 */
export interface SpriteLookupDiagnostic {
  speciesId: number;
  indexEntries: number;
  hasPayloadForKey: boolean[];
  staleIndexKeys: string[];
}

export function diagnoseSpriteLookup(speciesId: number): SpriteLookupDiagnostic {
  const all = listStoredSprites().filter((e) => e.speciesId === speciesId);
  const hasPayload: boolean[] = [];
  const stale: string[] = [];
  for (const m of all) {
    const present = readLocal(KEY_PREFIX + m.key) !== null;
    hasPayload.push(present);
    if (!present) stale.push(m.key);
  }
  return {
    speciesId,
    indexEntries: all.length,
    hasPayloadForKey: hasPayload,
    staleIndexKeys: stale,
  };
}

/**
 * Total bytes currently stored across the index. Used by diagnostics +
 * the LRU bound; cheap because the index already records per-entry size.
 */
export function storedSpriteBytes(): number {
  return readIndex().entries.reduce((sum, e) => sum + (e.bytes || 0), 0);
}

/**
 * Drop every sprite the store knows about. Exposed for a future
 * "clear local data" privacy control + for tests.
 */
export function clearAllStoredSprites(): number {
  const idx = readIndex();
  for (const e of idx.entries) removeLocal(KEY_PREFIX + e.key);
  writeIndex({ entries: [] });
  return idx.entries.length;
}
